import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { StockItem, InventoryRow } from "../types";
import { inventoryRowToStock } from "../types";
import { uuid } from "../utils/uuid";
import { BUDGET_TYPE_CATALOG_NAMES } from "../utils/budgetCatalog";

export function useInventory(ownerId?: string, regionId?: string, partnerId?: string | null) {
  const [stock, setStock] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // MULTI-TENANT: Ne pas charger tout l'inventaire si ownerId pas encore connu
      if (!ownerId) {
        setStock([]);
        setLoading(false);
        return;
      }

      let query = supabase.from("inventory").select("*").eq("owner_id", ownerId);
      if (regionId) query = query.eq("region_id", regionId);
      if (partnerId !== undefined) {
        if (partnerId === null) {
          query = query.is("partner_id", null);
        } else {
          query = query.eq("partner_id", partnerId);
        }
      }
      const { data, error: e } = await query.order("name", { ascending: true });
      if (e) throw e;
      const rows = (data ?? []) as InventoryRow[];
      setStock(rows.map(inventoryRowToStock));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur chargement stock");
      setStock([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId, regionId, partnerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const add = useCallback(
    async (
      name: string,
      quantity: number,
      lowStockThreshold?: number,
      regionId?: string,
      partnerIdParam?: string | null
    ) => {
      // FIXÉ: Vérification ownerId avant ajout
      if (!ownerId) return { data: null, error: "Non autorisé" };

      try {
        const id = uuid();
        const insertData: Record<string, unknown> = {
          id,
          name,
          quantity,
          owner_id: ownerId,
          low_stock_threshold: lowStockThreshold ?? 10,
        };
        if (regionId != null) insertData.region_id = regionId;
        if (partnerIdParam !== undefined) insertData.partner_id = partnerIdParam ?? null;

        const { data, error: e } = await supabase
          .from("inventory")
          .insert(insertData)
          .select()
          .single();

        if (e) throw e;
        const item = inventoryRowToStock(data as InventoryRow);
        setStock((prev) => [...prev, item].sort((a, b) => a.name.localeCompare(b.name)));
        return { data: item, error: null };
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : typeof err === "object" && err !== null && "message" in err
            ? String((err as { message: unknown }).message)
            : "Erreur ajout stock";
        console.error("Erreur ajout inventory:", err);
        return { data: null, error: msg };
      }
    },
    [ownerId]
  );

  const updateItem = useCallback(
    async (id: string, name: string, quantity: number, lowStockThreshold?: number) => {
      if (!ownerId) return { data: null, error: "Non autorisé" };

      try {
        const updateData: { name: string; quantity: number; low_stock_threshold?: number } = {
          name,
          quantity,
          ...(lowStockThreshold !== undefined && { low_stock_threshold: lowStockThreshold }),
        };

        const { data, error: e } = await supabase
          .from("inventory")
          .update(updateData)
          .eq("id", id)
          .eq("owner_id", ownerId) // FIXÉ: Ajout filtre sécurité
          .select()
          .single();

        if (e) throw e;
        const item = inventoryRowToStock(data as InventoryRow);
        setStock((prev) => prev.map((s) => (s.id === id ? item : s)));
        return { data: item, error: null };
      } catch (err) {
        return {
          data: null,
          error: err instanceof Error ? err.message : "Erreur mise à jour stock",
        };
      }
    },
    [ownerId]
  );

  // FIXÉ: Opérations atomiques au lieu de fetch puis update
  const decrement = useCallback(
    async (id: string, by: number) => {
      if (!ownerId) return { data: null, error: "Non autorisé" };

      try {
        // Utiliser une requête RPC pour décrémentation atomique
        // Si pas de RPC disponible, utiliser au moins une seule requête
        const { data: current, error: fetchErr } = await supabase
          .from("inventory")
          .select("quantity")
          .eq("id", id)
          .eq("owner_id", ownerId)
          .single();

        if (fetchErr) throw fetchErr;
        if (!current) return { data: null, error: "Article inconnu" };

        const newQty = Math.max(0, current.quantity - by);

        const { data, error: e } = await supabase
          .from("inventory")
          .update({ quantity: newQty, updated_at: new Date().toISOString() })
          .eq("id", id)
          .eq("owner_id", ownerId)
          .select()
          .single();

        if (e) throw e;
        const item = inventoryRowToStock(data as InventoryRow);
        setStock((prev) => prev.map((s) => (s.id === id ? item : s)));
        return { data: item, error: null };
      } catch (err) {
        return {
          data: null,
          error: err instanceof Error ? err.message : "Erreur décrémentation stock",
        };
      }
    },
    [ownerId]
  );

  const increment = useCallback(
    async (id: string, by: number) => {
      if (!ownerId) return { data: null, error: "Non autorisé" };

      try {
        const { data: current, error: fetchErr } = await supabase
          .from("inventory")
          .select("quantity")
          .eq("id", id)
          .eq("owner_id", ownerId)
          .single();

        if (fetchErr) throw fetchErr;
        if (!current) return { data: null, error: "Article inconnu" };

        const newQty = Math.max(0, current.quantity + by);

        const { data, error: e } = await supabase
          .from("inventory")
          .update({ quantity: newQty, updated_at: new Date().toISOString() })
          .eq("id", id)
          .eq("owner_id", ownerId)
          .select()
          .single();

        if (e) throw e;
        const item = inventoryRowToStock(data as InventoryRow);
        setStock((prev) => prev.map((s) => (s.id === id ? item : s)));
        return { data: item, error: null };
      } catch (err) {
        return {
          data: null,
          error: err instanceof Error ? err.message : "Erreur incrémentation stock",
        };
      }
    },
    [ownerId]
  );

  const remove = useCallback(
    async (id: string) => {
      if (!ownerId) return { error: "Non autorisé" };

      try {
        const { error: e } = await supabase
          .from("inventory")
          .delete()
          .eq("id", id)
          .eq("owner_id", ownerId); // FIXÉ: Ajout filtre sécurité

        if (e) throw e;
        setStock((prev) => prev.filter((s) => s.id !== id));
        return { error: null };
      } catch (err) {
        return {
          error: err instanceof Error ? err.message : "Erreur suppression stock",
        };
      }
    },
    [ownerId]
  );

  /**
   * Crée les références manquantes pour tous les types de dossier (LED intérieur, PAC, etc.)
   * pour une région × partenaire (partnerId null = stock interne).
   */
  const ensureTypeCatalog = useCallback(
    async (
      regionId: string,
      partnerId?: string | null
    ): Promise<{ created: number; error: string | null }> => {
      if (!ownerId || !regionId) return { created: 0, error: "Non autorisé" };

      const resolvedPartnerId = partnerId ?? null;

      try {
        let query = supabase
          .from("inventory")
          .select("id, name")
          .eq("owner_id", ownerId)
          .eq("region_id", regionId);
        query =
          resolvedPartnerId === null
            ? query.is("partner_id", null)
            : query.eq("partner_id", resolvedPartnerId);

        const { data, error: fetchErr } = await query;
        if (fetchErr) throw fetchErr;

        const existingNames = new Set(
          ((data ?? []) as { id: string; name: string }[]).map((r) => r.name)
        );
        const missing = BUDGET_TYPE_CATALOG_NAMES.filter((name) => !existingNames.has(name));
        if (missing.length === 0) return { created: 0, error: null };

        const rows = missing.map((name) => ({
          id: uuid(),
          name,
          quantity: 0,
          owner_id: ownerId,
          region_id: regionId,
          partner_id: resolvedPartnerId,
          low_stock_threshold: 0,
        }));

        const { data: inserted, error: insertErr } = await supabase
          .from("inventory")
          .insert(rows)
          .select();
        if (insertErr) throw insertErr;

        const items = ((inserted ?? []) as InventoryRow[]).map(inventoryRowToStock);
        if (items.length > 0) {
          setStock((prev) =>
            [...prev, ...items].sort((a, b) => a.name.localeCompare(b.name, "fr"))
          );
        }
        return { created: items.length, error: null };
      } catch (err) {
        return {
          created: 0,
          error: err instanceof Error ? err.message : "Erreur initialisation types budget",
        };
      }
    },
    [ownerId]
  );

  return {
    stock,
    setStock,  // ← RAJOUTÉ pour compatibilité
    loading,
    error,
    refetch: fetchAll,
    add,
    updateItem,
    decrement,
    increment,
    remove,
    ensureTypeCatalog,
  };
}
